<template>
    <section class="GlobalActionScopeCard">
        <div class="GlobalActionScopeCard__title">
            {{ scopeSentence }}
        </div>
        <div class="GlobalActionScopeCard__outcomes">
            <div class="GlobalActionScopeCard__outcome">
                <div class="GlobalActionScopeCard__outcomeHeading">
                    <span class="GlobalActionScopeCard__dot GlobalActionScopeCard__dot--accept" />
                    If you accept
                </div>
                <div class="GlobalActionScopeCard__outcomeBody">
                    The {{ valueLabel }} shown below is <strong>accepted</strong> as {{ rolePhrase }} for {{ targetPhrase }}
                    and any future submissions with this exact value.
                </div>
            </div>
            <div class="GlobalActionScopeCard__outcome">
                <div class="GlobalActionScopeCard__outcomeHeading">
                    <span class="GlobalActionScopeCard__dot GlobalActionScopeCard__dot--reject" />
                    If you reject
                </div>
                <div class="GlobalActionScopeCard__outcomeBody">
                    The {{ valueLabel }} shown below is <strong>rejected</strong> as {{ rolePhrase }} for {{ targetPhrase }}
                    and any future submissions with this exact value.
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss">
.GlobalActionScopeCard {
    background: var(--eidWhite);
    border-radius: 8px;
    box-shadow: 0 1px 3px rgb(0 0 0 / 8%);
    overflow: hidden;

    &__title {
        padding: 16px 24px;
        color: var(--eidSecureBlue);
        font-size: var(--text-body-size, 14px);
        font-weight: var(--text-subheader-weight, 600);
    }

    &__outcomes {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        border-top: 1px solid var(--eidTechGray-10);
    }

    &__outcome {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 18px 24px;

        & + & {
            border-left: 1px solid var(--eidTechGray-10);
        }
    }

    &__outcomeHeading {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--eidSecureBlue);
        font-size: var(--text-small-size, 13px);
        font-weight: var(--text-section-header-weight, 600);
    }

    &__outcomeBody {
        color: var(--eidQualityBlack);
        font-size: var(--text-small-size, 13px);
        line-height: var(--text-body-line-height, 1.5);
    }

    &__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;

        &--accept {
            background: var(--eidTrustGreen);
        }

        &--reject {
            background: var(--eidDangerRed);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    export default defineComponent({
        name: 'GlobalActionScopeCard',
        props: {
            entityCount: {
                type: Number,
                required: true,
            },
            fieldLabel: {
                type: String,
                required: true,
            },
            valueLabel: {
                type: String,
                default: 'value',
            },
            asPhrase: {
                type: String,
                default: '',
            },
        },
        computed: {
            rolePhrase(): string {
                return this.asPhrase || `the ${this.fieldLabel}`;
            },
            scopeSentence(): string {
                const noun = this.entityCount === 1 ? 'entity' : 'entities';
                return `${this.entityCount} ${noun} will be affected by this decision.`;
            },
            targetPhrase(): string {
                return this.entityCount === 1 ? 'this entity' : `all ${this.entityCount} entities`;
            },
        },
    });
</script>
